import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Divider,
  Paper,
  Box,
  Alert,
  Badge,
  Progress
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { BulkConfirmModalProps } from './BulkConfirmModal.types';
import { BusinessSearchAutocomplete } from '../BusinessSearchAutocomplete';
import { BusinessSummary } from '../../../../domain/entities/Business';
import { useConfirmMention } from '../../../../application/queries/mentionQueries';

/**
 * BulkConfirmModal Component
 *
 * Workflow for confirming multiple mentions to a single business
 *
 * Features:
 * - Display count of mentions to confirm
 * - Business search with debounced autocomplete
 * - Preview selected business
 * - Confirm button (disabled until business selected)
 * - Progress indicator during bulk confirmation
 * - Partial success handling
 * - Auto-close on completion
 *
 * Session 006: Created for bulk actions
 *
 * Workflow:
 * 1. User selects multiple pending mentions
 * 2. User clicks "Bulk Confirm"
 * 3. Modal opens with count of mentions
 * 4. User searches and selects ONE business
 * 5. Preview shows selected business
 * 6. User clicks "Confirm All"
 * 7. Progress indicator shows completion
 * 8. Success: modal closes, list auto-refreshes
 *
 * Usage:
 * ```tsx
 * <BulkConfirmModal
 *   mentionIds={selectedMentionIds}
 *   opened={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
export const BulkConfirmModal: React.FC<BulkConfirmModalProps> = ({
  mentionIds,
  opened,
  onClose
}): React.ReactElement => {
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessSummary | null>(null);
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ current: number; total: number }>({
    current: 0,
    total: 0
  });
  const confirmMutation = useConfirmMention();

  /**
   * Handle business selection from autocomplete
   */
  const handleBusinessSelect = useCallback((business: BusinessSummary | null): void => {
    setSelectedBusiness(business);
  }, []);

  /**
   * Handle bulk confirm
   */
  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!selectedBusiness) {
      return;
    }

    setIsConfirming(true);
    setProgress({ current: 0, total: mentionIds.length });

    let successCount = 0;
    let errorCount = 0;

    // Execute confirmations sequentially
    for (let i = 0; i < mentionIds.length; i++) {
      const mentionId = mentionIds[i];
      if (!mentionId) continue;

      try {
        await confirmMutation.mutateAsync({
          mentionId,
          businessId: selectedBusiness.id
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to confirm mention ${mentionIds[i]}:`, error);
        errorCount++;
      }
      setProgress({ current: i + 1, total: mentionIds.length });
    }

    // Show result notification
    if (errorCount === 0) {
      console.log(`Success: ${successCount} mention${successCount !== 1 ? 's' : ''} confirmed`);
    } else {
      console.warn(`Partial success: Confirmed ${successCount}, Failed ${errorCount}`);
    }

    // Reset state and close modal
    setIsConfirming(false);
    setSelectedBusiness(null);
    setProgress({ current: 0, total: 0 });
    onClose();
  }, [selectedBusiness, mentionIds, confirmMutation, onClose]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback((): void => {
    if (isConfirming) {
      // Prevent closing while operation in progress
      return;
    }
    setSelectedBusiness(null);
    setProgress({ current: 0, total: 0 });
    onClose();
  }, [isConfirming, onClose]);

  /**
   * Calculate progress percentage
   */
  const progressPercentage =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>Bulk Confirm Mentions</Title>}
      size="lg"
      padding="lg"
      closeOnClickOutside={!isConfirming}
      closeOnEscape={!isConfirming}
    >
      <Stack gap="md">
        {/* Information Alert */}
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          You are about to confirm <Text component="span" fw={700}>{mentionIds.length} mention{mentionIds.length !== 1 ? 's' : ''}</Text> to a single
          business. Search and select the business below.
        </Alert>

        {/* Progress Indicator (shown during confirmation) */}
        {isConfirming && (
          <Paper p="md" withBorder radius="md" bg="blue.0">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  Confirming mentions...
                </Text>
                <Text size="sm" c="dimmed">
                  {progress.current} of {progress.total}
                </Text>
              </Group>
              <Progress value={progressPercentage} size="lg" animated />
            </Stack>
          </Paper>
        )}

        <Divider label="Select Business" labelPosition="center" />

        {/* Business Search */}
        <BusinessSearchAutocomplete
          value={selectedBusiness}
          onChange={handleBusinessSelect}
          label="Business"
          placeholder="Search for a business..."
          description="Type at least 2 characters to search"
          required
          disabled={isConfirming}
        />

        {/* Business Preview */}
        {selectedBusiness && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="xs">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>
                Selected Business
              </Text>
              <Group justify="space-between" align="start">
                <Box>
                  <Text size="md" fw={600}>
                    {selectedBusiness.name}
                  </Text>
                  {selectedBusiness.category && (
                    <Text size="sm" c="dimmed">
                      {selectedBusiness.category}
                    </Text>
                  )}
                  {(selectedBusiness.formattedAddress ||
                    selectedBusiness.addressLine1 ||
                    selectedBusiness.city) && (
                    <Text size="sm" c="dimmed" mt={4}>
                      {selectedBusiness.formattedAddress ||
                        [selectedBusiness.addressLine1, selectedBusiness.city, selectedBusiness.state]
                          .filter(Boolean)
                          .join(', ')}
                    </Text>
                  )}
                </Box>
                {selectedBusiness.regionName && (
                  <Badge variant="light" color="violet">
                    {selectedBusiness.regionName}
                  </Badge>
                )}
              </Group>
            </Stack>
          </Paper>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="space-between">
          <Button variant="default" onClick={handleClose} disabled={isConfirming}>
            Cancel
          </Button>

          <Button
            leftSection={<IconCheck size={16} />}
            color="green"
            onClick={handleConfirm}
            disabled={!selectedBusiness || isConfirming}
            loading={isConfirming}
          >
            Confirm All ({mentionIds.length})
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
